import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { TransferDistributionMode } from "@redotech/redo-model/wms/wms-constants";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import {
  FormMultiSelectDropdown,
  FormSelectDropdown,
} from "@redotech/redo-web/select-dropdown";
import { FormSwitch } from "@redotech/redo-web/switch";
import { Text } from "@redotech/redo-web/text";
import { InputProvider, groupInput, input } from "@redotech/ui/form";
import { memo } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { OutboundLabelForm } from "../outbound-labels";

export const wmsEnabledForm = groupInput({
  enabled: input<boolean>(),
  packingParcelsEnabled: input<boolean>(),
  binCoordinateMonitoringEnabled: input<boolean>(),
  binCoordinateMonitoringAlertThresholdPercent: input<number>(),
  sourceOfTruthEnabled: input<boolean>(),
  inventoryDriftMonitoringLocationIds: input<string[]>(),
  transferDistributionMode: input<TransferDistributionMode>(),
});

export type WmsEnabledForm = InputProvider.Form<typeof wmsEnabledForm>;

export type WmsEnabledValue = InputProvider.Value<typeof wmsEnabledForm>;

export const wmsEnabledDefault: WmsEnabledValue = {
  enabled: false,
  packingParcelsEnabled: false,
  binCoordinateMonitoringEnabled: false,
  binCoordinateMonitoringAlertThresholdPercent: 80,
  sourceOfTruthEnabled: false,
  inventoryDriftMonitoringLocationIds: [],
  transferDistributionMode: TransferDistributionMode.BIN_FIRST,
};

const TRANSFER_DISTRIBUTION_MODE_OPTIONS = Object.values(
  TransferDistributionMode,
);

const TRANSFER_DISTRIBUTION_MODE_LABELS: Record<
  TransferDistributionMode,
  string
> = {
  [TransferDistributionMode.BIN_FIRST]: "Bin first (default)",
  [TransferDistributionMode.ITEM_FIRST]: "Item first",
};

export const WmsEnabledCard = memo(function WmsEnabledCard({
  input,
}: {
  input: OutboundLabelForm;
}) {
  const { wmsEnabled } = input.inputs;
  const params = useParams();
  const teamId = params.teamId!;
  const client = useRequiredContext(RedoAdminRpcClientContext);

  const crdbLocationsLoad = useLoad(async () => {
    const result = await client.getTeamCrdbLocations({ teamId });
    return result.locations;
  }, [client, teamId]);

  const locationOptions = crdbLocationsLoad.value ?? [];

  return (
    <Card title="WMS Settings" w="full">
      <Flex dir="column" gap="md">
        <Text fontSize="sm">
          Enable WMS (Warehouse Management System) functionality for pick
          sessions and warehouse operations.
        </Text>
        <FormSwitch input={wmsEnabled.inputs.enabled} label="Enable WMS" />
        <FormSwitch
          input={wmsEnabled.inputs.packingParcelsEnabled}
          label="Enable packing parcels"
        />
        <FormSwitch
          input={wmsEnabled.inputs.sourceOfTruthEnabled}
          label="WMS Source of Truth"
        />
        {wmsEnabled.value.sourceOfTruthEnabled && (
          <RedoAlert
            color="warning"
            description="When enabled, WMS inventory is used as the source of truth for bin assignments during pick wave generation. Pick sessions will use incremental inventory-aware picking. Only enable this for warehouses with fully maintained WMS inventory."
            showCloseButton={false}
            title="WMS Source of Truth is enabled"
          />
        )}
        <FormSwitch
          input={wmsEnabled.inputs.binCoordinateMonitoringEnabled}
          label="Enable bin coordinate monitoring"
        />
        {wmsEnabled.value.binCoordinateMonitoringEnabled && (
          <RedoTextInput
            description="Alert when the percentage of line items with bin coordinates falls below this threshold"
            inputTypeHint="number"
            label="Alert threshold (%)"
            max={100}
            min={0}
            setValue={(value) =>
              wmsEnabled.inputs.binCoordinateMonitoringAlertThresholdPercent.setValue(
                Number(value) || 0,
              )
            }
            value={String(
              wmsEnabled.value.binCoordinateMonitoringAlertThresholdPercent,
            )}
          />
        )}
        <FormMultiSelectDropdown
          description="Select warehouse locations to monitor for inventory drift, orphaned transit bins, and negative bins"
          display={(value) => {
            if (!value.length) {
              return "No locations selected";
            }
            const names = value.map((id) => {
              const loc = locationOptions.find((l) => l.id === id);
              return loc ? loc.name : id;
            });
            return names.join(", ");
          }}
          input={wmsEnabled.inputs.inventoryDriftMonitoringLocationIds}
          label="Inventory drift monitoring locations"
          options={locationOptions.map((loc) => loc.id)}
        >
          {(id) => {
            const loc = locationOptions.find((l) => l.id === id);
            return loc ? `${loc.name} (${id})` : id;
          }}
        </FormMultiSelectDropdown>
        <FormSelectDropdown
          input={wmsEnabled.inputs.transferDistributionMode}
          label="Transfer distribution mode"
          options={TRANSFER_DISTRIBUTION_MODE_OPTIONS}
        >
          {(option) => TRANSFER_DISTRIBUTION_MODE_LABELS[option]}
        </FormSelectDropdown>
      </Flex>
    </Card>
  );
});
